import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '@siliconx/shared/ui/Card';
import { Button } from '@siliconx/shared/ui/Button';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import type { BenchCosmeticGrade, BenchRunDetail } from '@siliconx/shared';
import { setBenchCosmeticGrade } from '../../lib/bench';
import { qk } from '../../lib/queryKeys';

const GRADES: BenchCosmeticGrade[] = ['A', 'B', 'C', 'D'];

/** Cosmetic grade A-D. Any org user; every entry is kept as history and the newest is current. */
export function CosmeticGradePanel({ run }: { run: BenchRunDetail }) {
  const queryClient = useQueryClient();
  const current = run.cosmeticGrades[0]?.grade ?? null;
  const mutation = useMutation({
    mutationFn: (grade: BenchCosmeticGrade) => setBenchCosmeticGrade(run.id, grade),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: qk.bench.run(run.id) }),
  });

  return (
    <Card className="space-y-3">
      <PanelTitle title="Cosmetic grade" />
      <p className="text-sm">{current ? `Current grade: ${current}` : 'No cosmetic grade yet.'}</p>
      <div className="flex gap-2">
        {GRADES.map((g) => (
          <Button
            key={g}
            size="sm"
            variant={g === current ? 'primary' : 'secondary'}
            disabled={mutation.isPending}
            onClick={() => mutation.mutate(g)}
          >
            {g}
          </Button>
        ))}
      </div>
      {mutation.isError && <p className="text-sm text-status-red">The grade was not saved. Try again.</p>}
      {run.cosmeticGrades.length > 0 && (
        <ul className="text-xs text-muted-foreground">
          {run.cosmeticGrades.map((g) => (
            <li key={`${g.createdAt}-${g.grade}`}>
              {g.grade} - {new Date(g.createdAt).toLocaleString()} - {g.setByMe ? 'you' : 'another user'}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
